import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import {
  buildPieceTagInput,
  printBatchPieceTags,
} from "views/production/shared/generatePieceTag";

/**
 * DashboardStore (homepage at /app/home)
 *
 * Drives the plant-wide homepage. All values are MobX computeds derived from
 * the three feature areas that are actually live today:
 *   - Production Schedule (panelScheduleChipsStore, productionScheduleStore)
 *   - Master Piece List (panelsStore — including per-panel `qcStatus`)
 *   - Jobs / Projects (projectsStore)
 *
 * Homepage focus: today's phase chips grouped by job, plus a hero KPI strip
 * and a "Print QR Codes for Today" action that builds tags through the same
 * shared generator MPL and the schedule slideout use.
 */

type AnyRecord = Record<string, unknown>;

interface MinimalServiceStore {
  entities: AnyRecord[];
  find?: (params: { query?: AnyRecord }) => Promise<unknown>;
}
type StoreSlot = MinimalServiceStore | undefined;

export interface KpiTile {
  title: string;
  value: string;
  subtitle?: string;
  valueColor?: string;
}

export interface TodayChipRow {
  chipId?: number | string;
  panelId: string | number;
  panelMark: string;
  phase: string;
  phaseLabel: string;
  moldInstanceCode: string;
  hours: number;
  completedAt: string | null;
  /** Free-text assignee name from the chip's `assignedTo` field, if any. */
  assignedTo: string | null;
  /** Crew size attached to the chip, if any. */
  crewSize: number | null;
}

export interface TodayJobGroup {
  projectId: string;
  projectKey: string; // job number / project number for the header
  projectName: string;
  chipCount: number;
  chips: TodayChipRow[];
}

const QC_ATTENTION_STATUSES = new Set(["Yellow", "Orange", "Red"]);

const PHASE_LABEL: Record<string, string> = {
  moldProduction: "Mold",
  setup: "Setup",
  casting: "Casting",
  finishing: "Finishing",
  shipping: "Shipping",
};

class DashboardStore {
  rootStore: RootStore;

  /**
   * Today's phase chips, loaded directly from the `production-schedule/chips`
   * endpoint (the same one ProductionScheduleGridStore uses). We don't read
   * from `panelScheduleChipsStore.entities` because that ServiceStore has a
   * strict find-query schema that rejects our requests.
   */
  todayChipsRaw: AnyRecord[] = [];
  isLoadingChips = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- raw store accessors --------------------------------------------

  private get projectsStore(): StoreSlot {
    return (this.rootStore as AnyRecord).projectsStore as StoreSlot;
  }
  private get panelsStore(): StoreSlot {
    return (this.rootStore as AnyRecord).panelsStore as StoreSlot;
  }
  private get productionScheduleStore(): StoreSlot {
    return (this.rootStore as AnyRecord).productionScheduleStore as StoreSlot;
  }
  private get panelScheduleChipsStore(): StoreSlot {
    return (this.rootStore as AnyRecord).panelScheduleChipsStore as StoreSlot;
  }
  private get moldsStore(): StoreSlot {
    return (this.rootStore as AnyRecord).moldsStore as StoreSlot;
  }

  // ---- shared helpers -------------------------------------------------

  private get todayStart(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
  /**
   * Today as YYYY-MM-DD in LOCAL time. `toISOString()` is UTC and would
   * return tomorrow's date in evening hours for users west of UTC, so we
   * format manually from local components.
   */
  get todayIso(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  /**
   * Chips visible to the homepage. Backed by `todayChipsRaw` (loaded via the
   * `production-schedule/chips` endpoint), with a fallback to the
   * ServiceStore's entities if anything else has populated it.
   */
  private get allChips(): AnyRecord[] {
    if (this.todayChipsRaw.length > 0) {
      return this.todayChipsRaw.filter((c) => c.date);
    }
    return (this.panelScheduleChipsStore?.entities ?? []).filter((c) => c.date);
  }
  private get panelsById(): Map<string, AnyRecord> {
    const m = new Map<string, AnyRecord>();
    for (const p of this.panelsStore?.entities ?? []) m.set(String(p.id), p);
    return m;
  }
  private get projectsById(): Map<string, AnyRecord> {
    const m = new Map<string, AnyRecord>();
    for (const p of this.projectsStore?.entities ?? []) m.set(String(p.id), p);
    return m;
  }

  // ---- Hero KPIs ------------------------------------------------------

  get piecesCastToday(): number {
    return this.allChips.filter(
      (c) =>
        c.phase === "casting" &&
        String(c.date).slice(0, 10) === this.todayIso &&
        c.completedAt
    ).length;
  }

  get castingsScheduledToday(): number {
    return this.allChips.filter(
      (c) =>
        c.phase === "casting" &&
        String(c.date).slice(0, 10) === this.todayIso
    ).length;
  }

  get hoursScheduledToday(): number {
    return this.allChips
      .filter((c) => String(c.date).slice(0, 10) === this.todayIso)
      .reduce((sum, c) => sum + Number(c.hours ?? 0), 0);
  }

  get shipmentsToday(): { total: number; dispatched: number } {
    const todays = this.allChips.filter(
      (c) =>
        c.phase === "shipping" &&
        String(c.date).slice(0, 10) === this.todayIso
    );
    const dispatched = todays.filter((c) => c.completedAt).length;
    return { total: todays.length, dispatched };
  }

  get qcAttentionCount(): number {
    return (this.panelsStore?.entities ?? []).filter((p) =>
      QC_ATTENTION_STATUSES.has(String(p.qcStatus ?? ""))
    ).length;
  }

  get activeProjectCount(): number {
    return (this.projectsStore?.entities ?? []).filter(
      (p) => p.status === "active" || p.status === "in_progress"
    ).length;
  }

  get heroKpis(): KpiTile[] {
    const ship = this.shipmentsToday;
    const qcAttn = this.qcAttentionCount;
    return [
      {
        title: "Pieces Cast Today",
        value: `${this.piecesCastToday} / ${this.castingsScheduledToday}`,
        subtitle:
          this.castingsScheduledToday > 0
            ? `${Math.round(
                (this.piecesCastToday /
                  Math.max(this.castingsScheduledToday, 1)) *
                  100
              )}% of scheduled`
            : "No castings scheduled",
        valueColor: "primary.main",
      },
      {
        title: "Hours Scheduled",
        value: this.hoursScheduledToday.toFixed(1),
        subtitle: "across all phases today",
        valueColor: "info.main",
      },
      {
        title: "Shipments Today",
        value: `${ship.dispatched} / ${ship.total}`,
        subtitle:
          ship.total === 0 ? "None scheduled" : "dispatched of scheduled",
        valueColor: "success.main",
      },
      {
        title: "QC Attention",
        value: String(qcAttn),
        subtitle: qcAttn === 0 ? "All green" : "Yellow / Orange / Red panels",
        valueColor: qcAttn === 0 ? "success.main" : "warning.main",
      },
      {
        title: "Active Projects",
        value: String(this.activeProjectCount),
        subtitle: "in production",
        valueColor: "primary.main",
      },
    ];
  }

  // ---- Today's chips, grouped by job ----------------------------------

  /** Raw chips dated today, regardless of phase. */
  get todayChips(): AnyRecord[] {
    return this.allChips.filter(
      (c) => String(c.date).slice(0, 10) === this.todayIso
    );
  }

  /**
   * Today's chips grouped by project. Each group includes the project's
   * display name and a list of chips with the panel mark resolved.
   */
  get todayChipsByProject(): TodayJobGroup[] {
    const panels = this.panelsById;
    const projects = this.projectsById;
    const groups = new Map<string, TodayJobGroup>();

    for (const c of this.todayChips) {
      const pid = String(c.projectId ?? c.project_id ?? "");
      if (!pid) continue;

      const panel = panels.get(String(c.panelId));
      const project = projects.get(pid);

      const phase = String(c.phase ?? "");
      const assignedRaw = c.assignedTo;
      const crewRaw = c.crewSize;
      const row: TodayChipRow = {
        chipId: c.id as number | string | undefined,
        panelId: String(c.panelId ?? ""),
        panelMark: String(panel?.panelMark ?? c.panelIdentifier ?? "—"),
        phase,
        phaseLabel: PHASE_LABEL[phase] ?? phase,
        moldInstanceCode: String(c.moldInstanceCode ?? ""),
        hours: Number(c.hours ?? 0),
        completedAt: c.completedAt ? String(c.completedAt) : null,
        assignedTo:
          typeof assignedRaw === "string" && assignedRaw.trim().length > 0
            ? assignedRaw.trim()
            : null,
        crewSize:
          typeof crewRaw === "number" && Number.isFinite(crewRaw) && crewRaw > 0
            ? crewRaw
            : null,
      };

      const existing = groups.get(pid);
      if (existing) {
        existing.chips.push(row);
        existing.chipCount += 1;
      } else {
        groups.set(pid, {
          projectId: pid,
          projectKey: String(
            project?.projectNumber ??
              project?.jobNumber ??
              c.projectName ??
              pid
          ),
          projectName: String(
            project?.projectName ?? project?.name ?? c.projectName ?? `Project ${pid}`
          ),
          chipCount: 1,
          chips: [row],
        });
      }
    }

    // Sort chips within each group: by phase order, then panel mark.
    const phaseOrder = ["moldProduction", "setup", "casting", "finishing", "shipping"];
    for (const g of groups.values()) {
      g.chips.sort((a, b) => {
        const ap = phaseOrder.indexOf(a.phase);
        const bp = phaseOrder.indexOf(b.phase);
        if (ap !== bp) return ap - bp;
        return a.panelMark.localeCompare(b.panelMark);
      });
    }

    // Sort groups by project key alphanumerically.
    return Array.from(groups.values()).sort((a, b) =>
      a.projectKey.localeCompare(b.projectKey, undefined, { numeric: true })
    );
  }

  // ---- Print QR codes for today ---------------------------------------

  /** Today's chips that are specifically in the casting phase. */
  get todayCastingChips(): AnyRecord[] {
    return this.todayChips.filter((c) => c.phase === "casting");
  }

  /** Count of unique panels with a casting chip today. Drives the button label/state. */
  get todayCastingPanelCount(): number {
    const ids = new Set<string>();
    for (const c of this.todayCastingChips) {
      const pid = String(c.panelId ?? "");
      if (pid) ids.add(pid);
    }
    return ids.size;
  }

  /**
   * Print one tag per UNIQUE panel that has a CASTING chip today. Other
   * phases (setup, finishing, shipping) are deliberately excluded — the
   * crew only wants tags for pieces actually being cast that day. Tags use
   * the shared generator MPL and the schedule slideout use, so any field
   * updates flow through.
   */
  printTodayCastingTags = async (): Promise<void> => {
    const chips = this.todayCastingChips;
    if (chips.length === 0) return;

    // The route fires refreshHomepageData() as a floating promise, so a fast
    // click on Print can land before the panels have hydrated — which used to
    // produce a whole BATCH of tags built from chip fields only. Close that
    // race here rather than in the button, so every caller is covered.
    const missing = chips.some((c) => !this.panelsById.has(String(c.panelId)));
    if (missing) await this.hydrateTodayPanels();

    const panels = this.panelsById;
    const projects = this.projectsById;
    const seen = new Set<string>();

    const pieces = [];
    for (const chip of chips) {
      const pid = String(chip.panelId);
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);

      const panel = panels.get(pid);
      const project = projects.get(
        String(chip.projectId ?? chip.project_id ?? "")
      );

      // Merge chip + panel + project so buildPieceTagInput can pick up any
      // field it knows about (mark, mold, project number, type, location,
      // color, finish, weight, volumes).
      pieces.push(
        buildPieceTagInput({
          ...chip,
          ...(panel ?? {}),
          projectNumber: project?.projectNumber,
          projectName: project?.projectName ?? project?.name,
        })
      );
    }

    if (pieces.length === 0) return;
    const filename = `casting-tags-${this.todayIso}.pdf`;
    await printBatchPieceTags(pieces, filename);
  };

  // ---- Hydration ------------------------------------------------------

  /**
   * Load today's chips through the `production-schedule/chips` endpoint —
   * the same custom service ProductionScheduleGridStore uses. Takes
   * `startDate`/`endDate` (not `$gte`/`$lte`), and returns either an array
   * of chips or `{ chips: [...] }`.
   */
  /**
   * Fetch the panel records behind today's chips, scoped to those panelIds.
   *
   * This replaced a blanket `panelsStore.find({ $limit: 500 })` that ran in
   * parallel with the chip load. Two problems with that: the home screen — the
   * app's landing route — pulled 500 arbitrary panels it mostly did not need,
   * and on a plant with more panels than that the pieces actually casting today
   * were frequently NOT among them. When printTodayCastingTags then missed the
   * panel, it built the tag from chip fields alone and the piece type silently
   * vanished from the print. That is what Josh reported on 2026-08-13 as "this
   * one doesn't have the updated piece type".
   *
   * The backend chips DTO now also carries panelType/location/level/color/finish,
   * so a miss here degrades gracefully rather than losing the field — this is
   * the belt to that braces.
   */
  async hydrateTodayPanels(): Promise<void> {
    if (!this.panelsStore?.find) return;

    const ids = Array.from(
      new Set(
        this.todayChipsRaw
          .map((c) => Number(c.panelId))
          .filter((n) => Number.isFinite(n)),
      ),
    );
    if (ids.length === 0) return;

    await this.panelsStore.find({
      query: { id: { $in: ids }, $limit: Math.max(ids.length, 100) },
    });
  }

  async loadTodayChips(): Promise<void> {
    const app = (this.rootStore as AnyRecord).app as
      | { service?: (path: string) => { find: (params: AnyRecord) => Promise<unknown> } }
      | undefined;
    if (!app?.service) return;

    runInAction(() => {
      this.isLoadingChips = true;
    });

    try {
      const service = app.service("production-schedule/chips");
      const result = (await service.find({
        query: { startDate: this.todayIso, endDate: this.todayIso },
      })) as AnyRecord | AnyRecord[] | { chips?: AnyRecord[] };

      const chips: AnyRecord[] = Array.isArray(result)
        ? result
        : ((result as { chips?: AnyRecord[] })?.chips ?? []);

      runInAction(() => {
        this.todayChipsRaw = chips;
        this.isLoadingChips = false;
      });
    } catch (e) {
      console.warn("[DashboardStore] loadTodayChips failed:", e);
      runInAction(() => {
        this.todayChipsRaw = [];
        this.isLoadingChips = false;
      });
    }
  }

  async refreshHomepageData(): Promise<void> {
    const safe = (p?: Promise<unknown>) =>
      p
        ? p.catch((e) => console.warn("[DashboardStore] find failed:", e))
        : Promise.resolve();

    const tasks: Promise<unknown>[] = [];

    // Today's chips through the custom endpoint that the production schedule
    // uses — the only one that the chip service actually accepts. The panel
    // hydration below is chained onto this rather than run beside it, because
    // it needs the chips' panelIds.
    tasks.push(safe(this.loadTodayChips().then(() => this.hydrateTodayPanels())));

    if (this.projectsStore?.find) {
      tasks.push(safe(this.projectsStore.find({ query: { $limit: 200 } })));
    }
    if (this.moldsStore?.find) {
      tasks.push(safe(this.moldsStore.find({ query: { $limit: 200 } })));
    }
    if (this.productionScheduleStore?.find) {
      tasks.push(
        safe(this.productionScheduleStore.find({ query: { $limit: 500 } }))
      );
    }

    await Promise.all(tasks);
  }
}

export default DashboardStore;
